/**
 * Seeded dice. The single source of combat randomness (ADR-0008 §9).
 *
 * Implementation: mulberry32 — small, fast, seedable, well-distributed over the
 * sample sizes the project test floor exercises. Determinism is contractual:
 * same seed + same call sequence → identical output across runs.
 *
 * `DieType` (ADR-0018 §3) lifts dice from uniform d6s to a typed, face-restricted
 * model. A die is defined by its allowed faces + per-face weights; a single roll
 * picks a face with probability `weight / Σweight`. `PLAIN_D6` (the default) is
 * six faces, weight 1 each — and its weighted roll is BYTE-IDENTICAL to the
 * historic `1 + floor(rng()*6)` (one `rng()` draw, same face per draw), so a
 * character carrying only plain d6 fights exactly as before HEX-116. A specialist
 * die restricts / reweights faces to GUARANTEE an ADR-0015 predicate.
 */

import { matches } from './predicates';
import type { DieFace, Predicate } from './types';

/** A seeded random generator. Returns a value in [0, 1). */
export type Rng = () => number;

export function createRng(seed: number): Rng {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) | 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Roll `count` d6, returning an array of integers in [1, 6]. The standalone
 * d6 path (EventFlow gamble). Equivalent to rolling `count` `PLAIN_D6` via `rollDie`. */
export function rollPool(rng: Rng, count: number): number[] {
  const dice: number[] = [];
  for (let i = 0; i < count; i++) {
    dice.push(1 + Math.floor(rng() * 6));
  }
  return dice;
}

// --------------------------------------------------------------------------
// Typed dice (ADR-0018 §3) — specialist / face-restricted dice
// --------------------------------------------------------------------------

/** One face of a `DieType` and its (positive integer) weight. */
export type FaceWeight = { face: DieFace; weight: number };

/** A die type: an allowed face set with per-face weighting (ADR-0018 §3). The
 * roll source for a combatant's pool. `PLAIN_D6` is the default; specialist
 * types narrow / reweight `faces` to guarantee a predicate. Pure JSON data
 * (round-trips structurally — see serialise / persistence). */
export type DieType = {
  id: string;
  name: string;
  /** Faces this die can roll; each weight > 0. Roll picks face ∝ weight. A face
   * absent from this list is unrollable — the mechanism behind a guarantee. */
  faces: FaceWeight[];
};

/** The default uniform six-sided die. A character's starter dice are all
 * `PLAIN_D6`, so combat is identical to the pre-HEX-116 fixed-d6 roll. */
export const PLAIN_D6: DieType = {
  id: 'd6',
  name: 'd6',
  faces: [
    { face: 1, weight: 1 },
    { face: 2, weight: 1 },
    { face: 3, weight: 1 },
    { face: 4, weight: 1 },
    { face: 5, weight: 1 },
    { face: 6, weight: 1 },
  ],
};

/** Deterministically roll one `die` under `rng`. Consumes exactly one `rng()`
 * draw — so substituting `rollDie(rng, PLAIN_D6)` for the historic
 * `1 + floor(rng()*6)` preserves both the RNG step count and the exact face
 * sequence (`r = rng()*6`, subtract 1 per face → `floor(6·rng()) + 1`). */
export function rollDie(rng: Rng, die: DieType): DieFace {
  let total = 0;
  for (const fw of die.faces) total += fw.weight;
  let r = rng() * total;
  let last: FaceWeight | undefined;
  for (const fw of die.faces) {
    r -= fw.weight;
    if (r < 0) return fw.face;
    last = fw;
  }
  // Float-rounding safety: r never reaches `total` exactly, but guard the tail.
  // `last` is defined whenever `die.faces` is non-empty (every die has faces).
  if (last === undefined) throw new Error(`die ${die.id} has no faces`);
  return last.face;
}

/** Roll a pool of typed dice in order — one `rng()` draw each, one face per die.
 * A pool of `PLAIN_D6` is byte-identical to `rollPool` over the same `rng`. */
export function rollDicePool(rng: Rng, dice: readonly DieType[]): DieFace[] {
  return dice.map((die) => rollDie(rng, die));
}

/** True when `die` GUARANTEES `predicate`: every face it can roll satisfies it.
 * The deck-building lever (ADR-0018 §3) — a die that guarantees a predicate is
 * powerful for the cards that need it (a ones-die guarantees `exact:1` for
 * Brace; an evens-die guarantees `parity:even`). A `PLAIN_D6` guarantees only
 * trivially-true predicates (`min:1`, `max:6`). */
export function dieGuarantees(die: DieType, predicate: Predicate): boolean {
  return die.faces.every((fw) => matches(fw.face, predicate));
}
